import {
    Component,
    EventEmitter,
    Injector,
    Input,
    InputSignal,
    OnInit,
    Output,
    Signal,
    computed,
    effect,
    input,
    signal,
    untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { IqControlTextStateService } from '../state';
import { toSignal } from '@angular/core/rxjs-interop';
import { round } from 'lodash';

@Component({
    selector: 'iq-control-text-percent',
    standalone: true,
    imports: [CommonModule, NgxMaskDirective, ReactiveFormsModule],
    providers: [provideNgxMask()],
    templateUrl: './iq-control-text-percent.component.html',
    styleUrl: './iq-control-text-percent.component.scss',
})
export class IqControlTextPercentComponent implements OnInit {
    randomName: string =
        'iq-control-text-percent-' + Math.random().toString(36).substring(7);

    @Input()
    fg: FormGroup = new FormGroup({});

    @Output()
    changed: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    keyupped: EventEmitter<number> = new EventEmitter<number>();

    keyUp($event: any) {
        const correctedPercent = round(this.percent() / 100, 5);
        if (this.disabled() || this._isValueOutOfBounds(correctedPercent)) return;

        this.keyupped.emit(correctedPercent);
    }

    internalFormGroup = this._fb.group({});

    inputControl: FormControl;

    errorMessageSignal: Signal<string> = signal('');
    errorMessage: Signal<string> = computed((): string => {
        return this.errorMessageSignal();
    });
    passedValue: Signal<number> = signal(0);

    // Call this in the OnInit Function
    private _setErrorMessageSignal(): void {
        this.errorMessageSignal = this._state.errorMessage(this.inputControl);
    }

    id: InputSignal<string> = input('');
    placeholder: InputSignal<string> = input('');
    value: InputSignal<number> = input(0);
    name: InputSignal<string> = input('');
    disabled: InputSignal<boolean> = input(null);
    minValue: InputSignal<number> = input(null);
    maxValue: InputSignal<number> = input(null);
    required: InputSignal<boolean> = input(false);

    percentSignal: Signal<number> = signal(0);
    percent: Signal<number> = computed((): number => {
        return this.percentSignal();
    });

    // Call this in the OnInit Function
    private _setPercentSignal(): void {
        this.percentSignal = this._state.percent(
            this.name,
            this.value,
            this.internalFormGroup,
            {
                disabled: this.disabled,
                minValue: this.minValue,
                maxValue: this.maxValue,
                required: this.required,
            },
        );
    }

    private _isValueOutOfBounds(correctedPercent: number): boolean {
        const hasMinOrMaxValue = !!this.minValue() || !!this.maxValue();
            return hasMinOrMaxValue &&
                (correctedPercent < this.minValue() 
                    || correctedPercent > this.maxValue());
    }

    constructor(
        private readonly _state: IqControlTextStateService,
        private readonly _fb: FormBuilder,
        private readonly _injector: Injector,
    ) {
        effect(() => {
            const correctedPercent = round(this.percent() / 100, 5);
            if (this.disabled() || this._isValueOutOfBounds(correctedPercent)) return;

            untracked(() => {
                this.fg.get(this.name()).setValue(correctedPercent);
            });

            this.changed.emit(correctedPercent);
        });

        effect(() => {
            const passedValue = this.passedValue();
            
            untracked(() => {
                this.inputControl.setValue(passedValue * 100);
            });
        });
    }

    ngOnInit(): void {
        this._setPercentSignal();

        this.inputControl = this.internalFormGroup.get(
            this.name(),
        ) as FormControl;

        this.fg.addControl(this.name(), this._fb.control(this.value()));
        this.passedValue = toSignal(this.fg.get(this.name()).valueChanges, {
            injector: this._injector,
        });

        this._setErrorMessageSignal();
    }
}
